import type { ApiSessionData } from '../api/types';
import type { DcId, SharedSessionData } from '../types';

import {
  DC_IDS,
  DEBUG, IS_SCREEN_LOCKED_CACHE_KEY,
  MULTIACCOUNT_MAX_SLOTS,
  SESSION_ACCOUNT_PREFIX,
  SESSION_LEGACY_USER_KEY,
} from '../config';
import { ACCOUNT_SLOT, storeAccountData, writeSlotSession } from './multiaccount';

export function hasStoredSession() {
  if (checkSessionLocked()) {
    return true;
  }

  if (!ACCOUNT_SLOT) {
    const legacyAuthJson = localStorage.getItem(SESSION_LEGACY_USER_KEY);
    if (legacyAuthJson) {
      try {
        const userAuth = JSON.parse(legacyAuthJson);
        // Check only for dcID, not userId - userId is set later via updateSessionUserId
        return Boolean(userAuth && userAuth.dcID);
      } catch (err) {
        // Do nothing.
        return false;
      }
    }
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);
  return Boolean(slotData && slotData.dcId);
}

/**
 * Whether the slot belongs to a logged-in user, not just an auth key persisted on the login screen
 * (SPAC-2204), which has no `userId`. The one rule for "slot is logged in" in all slot redirects
 * (`App.tsx`, `Auth.tsx`, sign-out, the account menu); diverging rules made slots bounce (SPAC-2595)
 * or hid a logged-in account behind a login screen (SPAC-2771).
 */
export function isSlotLoggedIn(slot: number | undefined) {
  if (loadSlotSession(slot)?.userId) return true;

  if (!slot || slot === 1) {
    try {
      return Boolean(JSON.parse(localStorage.getItem(SESSION_LEGACY_USER_KEY) || 'null')?.id);
    } catch (err) {
      return false;
    }
  }

  return false;
}

/**
 * Whether the stored session of the current slot belongs to a logged-in user (see `isSlotLoggedIn`).
 */
export function hasStoredUserId() {
  return isSlotLoggedIn(ACCOUNT_SLOT);
}

/**
 * Finds the first slot with an active session.
 * Prioritizes slot 1, then checks slots 2-3.
 * A slot counts only with a logged-in user (`isSlotLoggedIn`).
 * @param currentSlot - The current slot to skip in the search
 * @returns The slot number with active session, or undefined if none found
 */
export function findFirstActiveSlot(currentSlot?: number): number | undefined {
  // Always check slot 1 first if not current
  if (currentSlot !== 1 && isSlotLoggedIn(1)) {
    return 1;
  }

  // Check other slots (2-3)
  for (let i = 2; i <= MULTIACCOUNT_MAX_SLOTS; i++) {
    if (i === currentSlot) continue;
    if (isSlotLoggedIn(i)) {
      return i;
    }
  }

  return undefined;
}

// Cached auth states in which slot 1 keeps the page: a login in progress, or an already logged-in account
const SLOT_1_KEEPING_AUTH_STATES = new Set<string>([
  'authorizationStateWaitCode',
  'authorizationStateWaitPassword',
  'authorizationStateWaitRegistration',
  'authorizationStateReady',
]);

/**
 * Slot to open instead of slot 1 when slot 1 has no logged-in user but another slot has one.
 * A pre-login key in slot 1 (left by any `#login` visit) does not keep the page, nor does the cached login
 * screen state. Explicit `#login`, passcode and web auth token are checked by the caller.
 * @param authState - `authState` of slot 1 restored from the cache, if any
 */
export function findSlotToLeaveSlot1For(authState?: string): number | undefined {
  if (checkSessionLocked() || isSlotLoggedIn(1)) return undefined;
  if (authState && SLOT_1_KEEPING_AUTH_STATES.has(authState)) return undefined;

  return findFirstActiveSlot(1);
}

export function storeSession(sessionData: ApiSessionData) {
  const {
    mainDcId, keys, isTest,
  } = sessionData;

  const currentSlotData = loadSlotSession(ACCOUNT_SLOT);
  const newSlotData: SharedSessionData = {
    ...currentSlotData,
    dcId: mainDcId,
    isTest: isTest ? true : undefined,
  };

  Object.keys(keys).map(Number).forEach((dcId) => {
    const key = keys[dcId];
    newSlotData[`dc${dcId as DcId}_auth_key`] = typeof key === 'string' ? key : JSON.stringify(key);
  });

  writeSlotSession(ACCOUNT_SLOT, newSlotData);

  // Store legacy session for slot 1 after writing slot data
  // This ensures userId from newSlotData is available
  if (!ACCOUNT_SLOT) {
    const updatedSlotData = loadSlotSession(ACCOUNT_SLOT);
    storeLegacySession(sessionData, updatedSlotData?.userId);
  }
}

function storeLegacySession(sessionData: ApiSessionData, currentUserId?: string) {
  const {
    mainDcId, keys, isTest,
  } = sessionData;

  localStorage.setItem(SESSION_LEGACY_USER_KEY, JSON.stringify({
    dcID: mainDcId,
    id: currentUserId,
    test: isTest,
  }));
  localStorage.setItem('dc', String(mainDcId));
  Object.keys(keys).map(Number).forEach((dcId) => {
    localStorage.setItem(`dc${dcId}_auth_key`, JSON.stringify(keys[dcId]));
  });
}

export function clearStoredSession(slot?: number) {
  const targetSlot = slot ?? ACCOUNT_SLOT;

  // Clear the slot session data
  localStorage.removeItem(`${SESSION_ACCOUNT_PREFIX}${targetSlot || 1}`);

  // Also clear legacy session for slot 1 (first account without ?account= param)
  if (!targetSlot) {
    clearStoredLegacySession();
  }
}

function clearStoredLegacySession() {
  [
    SESSION_LEGACY_USER_KEY,
    'dc',
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);

  if (!slotData) {
    if (ACCOUNT_SLOT) {
      return undefined;
    }
    return loadStoredLegacySession();
  }

  const sessionData: ApiSessionData = {
    mainDcId: slotData.dcId,
    keys: DC_IDS.reduce((acc, dcId) => {
      const key = slotData[`dc${dcId}_auth_key` as const];
      if (key) {
        acc[dcId] = key;
      }
      return acc;
    }, {} as Record<number, string>),
    isTest: slotData.isTest || undefined,
    hashes: {},
  };

  return sessionData;
}

function loadStoredLegacySession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const userAuth = JSON.parse(localStorage.getItem(SESSION_LEGACY_USER_KEY) || 'null');
  if (!userAuth) {
    return undefined;
  }
  const mainDcId = Number(userAuth.dcID);
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};

  DC_IDS.forEach((dcId) => {
    try {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load stored session', err);
      }
      // Do nothing.
    }
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId,
    keys,
    isTest,
    hashes: {},
  };
}

export function loadSlotSession(slot: number | undefined): SharedSessionData | undefined {
  const key = `${SESSION_ACCOUNT_PREFIX}${slot || 1}`;
  try {
    const raw = localStorage.getItem(key);
    const data = JSON.parse(raw || '{}') as SharedSessionData;
    if (!data.dcId) {
      return undefined;
    }
    return data;
  } catch (e) {
    return undefined;
  }
}

export function updateSessionUserId(currentUserId: string) {
  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (!slotData) return;
  storeAccountData(ACCOUNT_SLOT, { userId: currentUserId });

  // Update legacy session userId for slot 1
  if (!ACCOUNT_SLOT) {
    const legacyAuthJson = localStorage.getItem(SESSION_LEGACY_USER_KEY);
    if (legacyAuthJson) {
      try {
        const userAuth = JSON.parse(legacyAuthJson);
        userAuth.id = currentUserId;
        localStorage.setItem(SESSION_LEGACY_USER_KEY, JSON.stringify(userAuth));
      } catch (err) {
        // Do nothing
      }
    }
  }
}

export function importTestSession() {
  const sessionJson = process.env.TEST_SESSION!;
  try {
    const sessionData = JSON.parse(sessionJson) as ApiSessionData & { userId: string };
    storeLegacySession(sessionData, sessionData.userId);
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load test session', err);
    }
  }
}

function checkSessionLocked() {
  return localStorage.getItem(IS_SCREEN_LOCKED_CACHE_KEY) === 'true';
}
